// Third party libraries
import _ from "lodash";
import { useState, useEffect, useCallback } from "react";
import { connect, useDispatch } from "react-redux";
import {
    prepareRulesEngine,
    RulesEngineBuilder
} from "@react-querybuilder/rules-engine";
import { QueryBuilderStateProvider } from "react-querybuilder";
import { Button, Checkbox, Form, Header } from "semantic-ui-react";
import { Field, reduxForm } from "redux-form";

// Models
import SessionModel from "../../../../../../../../data/models/sessions";

// Redux
import { openLoader, closeLoader } from "../../../../../../../../redux-store/loader";
import { closeModal, openModal } from "../../../../../../../../redux-store/modal";

// Config
import { required } from "../../../../../../../../config/validations";

// Components
import RuleActionParams from "./actions";
import CustomInput from "../../../../../../../components/form/input";

// Locales
import I18n from "../../../../../../../../i18n";

// Constants
import { CONSEQUENT_TYPES, FIELDS, OPERATOR_MAP, REVERSE_OPERATOR_MAP } from "./constants";

// Styles
import './form.scss';

const RuleForm = (props) => {
    const dispatch = useDispatch();

    const [rulesEngine, setRulesEngine] = useState({
        conditions: [
            {
                id: 'root-condition',
                antecedent: { combinator: 'and', rules: [] },
                consequent: { type: 'adjust-time', data: {} }
            }
        ],
        defaultConsequent: { type: 'default-action' }
    });

    const storedToAntecedent = useCallback((stored) => {
        if (!stored) return { combinator: 'and', rules: [] };

        const key = stored.all ? 'all' : (stored.any ? 'any' : null);
        const combinator = key === 'any' ? 'or' : 'and';
        const items = key ? (stored[key] || []) : [];

        const rules = items.map(item => {
            // 1. Si es un grupo anidado (all/any), llamamos recursivamente
            if (item.all || item.any) return storedToAntecedent(item);

            // 2. Reconstruimos el nombre que la UI espera
            let uiField;

            // Verificamos si existe 'path' para evitar el error de 'undefined'
            if (item.fact === 'asana') {
                // Si no hay path por alguna razón, usamos solo el fact
                uiField = item.path
                    ? `asana.${item.path.replace('$.', '')}`
                    : 'asana';
            }
            else if (item.fact === 'user') {
                uiField = item.path ? `user.${item.path.replace('$.', '')}` : 'user';
            } else {
                // Para matrix, si hay path limpiamos el $
                // Si no hay path (regla vieja o plana), usamos el fact directamente
                uiField = item.path
                    ? item.path.replace('$.', '')
                    : item.fact;
            }

            const uiOperator = REVERSE_OPERATOR_MAP[item.operator] || item.operator;

            // --- CORRECCIÓN DE TIPO DE DATO ---
            let val = item.value;
            if (typeof val === 'string' && !isNaN(val) && val.trim() !== "") {
                val = Number(val);
            }

            return {
                field: uiField,
                operator: uiOperator,
                value: val
            };
        });

        return { combinator, rules };
    }, []);

    useEffect(() => {
        const init = props.initialValues;
        if (!init || !init.conditions) return;

        const antecedent = storedToAntecedent(init.conditions);

        const consequent = {
            type: _.get(init, 'event.type', 'adjust-time'),
            data: _.get(init, 'event.params', {})
        };

        // --- LA CLAVE: PREPARAR EL MOTOR ---
        const rawEngine = {
            conditions: [{
                id: 'root-condition',
                antecedent,
                consequent
            }],
            defaultConsequent: { type: 'default-action' }
        };

        // prepareRulesEngine recorre todo el objeto y añade los 'id' faltantes
        setRulesEngine(prepareRulesEngine(rawEngine));

    }, [props.initialValues, storedToAntecedent]);

    const manualFormatRulesEngine = (re) => {
        if (!re || !re.conditions) return [];

        // Filtramos solo las condiciones que ya tienen una acción (consequent) seleccionada
        return re.conditions
            .filter(c => c.consequent && c.consequent.type)
            .map(condition => {
                const antecedent = transformAntecedent(condition.antecedent);

                // Si no hay reglas válidas en el antecedente, ignoramos esta regla
                if (!antecedent) return null;

                return {
                    conditions: antecedent,
                    event: {
                        type: condition.consequent.type,
                        // Aquí es donde viviría tu "10%" o "15%"
                        params: condition.consequent.data || {}
                    }
                };
            })
            .filter(Boolean); // Limpiamos nulos
    };

    const transformAntecedent = (antecedent) => {
        if (!antecedent || !antecedent.rules || antecedent.rules.length === 0) return null;

        const combinator = antecedent.combinator === 'and' ? 'all' : 'any';

        const rules = antecedent.rules.map(rule => {
            if (rule.rules) return transformAntecedent(rule);

            const engineOperator = OPERATOR_MAP[rule.operator] || rule.operator;
            const uiField = rule.field; // ej: "asana.isAdvanced" o "toneAverage"

            let fact, path;

            if (uiField.startsWith('asana.')) {
                // Caso: Metadata de Asana
                fact = 'asana';
                path = `$.${uiField.replace('asana.', '')}`; // "$.isAdvanced"
            }
            else if (uiField.startsWith('user.')) {
                // Caso 2: Preferencias del Usuario (WIZARD) - ¡NUEVO!
                fact = 'user';
                path = `$.${uiField.replace('user.', '')}`; // "$.props" o "$.sensitivity.intensity"
            }
            else {
                // Caso: Datos de la Matriz (Globales o detailedAverages)
                fact = 'matrix';
                path = `$.${uiField}`; // "$.toneAverage" o "$.detailedAverages.toneAverages..."
            }

            return {
                fact,
                path,
                operator: engineOperator,
                value: rule.value
            };
        }).filter(Boolean);

        return rules.length > 0 ? { [combinator]: rules } : null;
    };

    const updateConsequentData = (index, key, value) => {
        setRulesEngine(prevEngine => {
            // Creamos un nuevo array de condiciones
            const newConditions = prevEngine.conditions.map((condition, i) => {
                // Solo modificamos la que coincide con el índice
                if (i === index) {
                    return {
                        ...condition,
                        consequent: {
                            ...condition.consequent,
                            // Creamos un nuevo objeto 'data' para evitar el error de extensión
                            data: {
                                ...(condition.consequent?.data || {}),
                                [key]: value
                            }
                        }
                    };
                }
                return condition;
            });

            const updatedEngine = {
                ...prevEngine,
                conditions: newConditions
            };

            return updatedEngine;
        });
    };

    const onSubmit = async formValues => {

        console.log(formValues)

        const message = {
            headerTitle: "Error",
            title: '',
            buttons: [{
                text: I18n.t('actions.accept'),
                callback: () => dispatch(closeModal())
            }]
        };

        try {

            const formattedRules = manualFormatRulesEngine(rulesEngine);
            const ruleData = formattedRules?.[0]; // Obtenemos nuestra única regla

            if (ruleData) {
                dispatch(openLoader());

                // Combinamos los valores del formulario (nombre, estado) con la regla
                const payload = {
                    ...ruleData,
                    name: formValues.name,
                    status: formValues.status
                };

                if (props.initialValues && props.initialValues._id) {

                    await SessionModel.editRule(props.initialValues._id, payload);

                } else {

                    await SessionModel.createRule(payload);

                }

                props.history.push('/home/personalized-sequences/rules');

            }

        } catch (error) {

            message.title = I18n.t('personalizedSequence.rules.anErrorOcurred') + ' ' + _.get(error, 'error', error);
            dispatch(openModal(message));

        } finally {

            dispatch(closeLoader());

        }
    };

    return (
        <Form onSubmit={props.handleSubmit(onSubmit)}>
            <Header className="ruleFormHeader" as='h3'>
                <span>Editor de Regla</span>
                <Field
                    name="status"
                    component={({ input, ...custom }) => (
                        <Checkbox
                            {...custom}
                            toggle
                            label={I18n.t('personalizedSequence.rules.active')}
                            // 'input.value' es el valor real en el estado de Redux Form
                            checked={!!input.value}
                            // Informamos a Redux Form del nuevo valor cada vez que cambia
                            onChange={(e, { checked }) => input.onChange(checked)}
                        />
                    )}
                />
            </Header>
            <Field
                className="ruleFormName"
                component={CustomInput}
                name="name"
                validate={[required]} />
            <QueryBuilderStateProvider>
                <RulesEngineBuilder
                    key={rulesEngine.id || 'loading'}
                    queryBuilderProps={{ fields: FIELDS, enableMountQueryChange: true }}
                    rulesEngine={rulesEngine}
                    defaultRulesEngine={rulesEngine}
                    onRulesEngineChange={(re) => {
                        setRulesEngine(re);
                    }}
                    consequentTypes={CONSEQUENT_TYPES}
                    translations={{
                        blockLabelIf: { label: I18n.t('personalizedSequence.rules.blockLabelIf') },
                        blockLabelElseIf: { label: I18n.t('personalizedSequence.rules.blockLabelElseIf') },
                        blockLabelElse: { label: I18n.t('personalizedSequence.rules.blockLabelElse') },
                        blockLabelThen: { label: I18n.t('personalizedSequence.rules.blockLabelThen') },
                        addCondition: { label: I18n.t('personalizedSequence.rules.addCondition') },
                        addSubcondition: { label: I18n.t('personalizedSequence.rules.addSubcondition') },
                        addConsequent: { label: I18n.t('personalizedSequence.rules.addConsequent') },
                        removeCondition: { label: I18n.t('personalizedSequence.rules.removeCondition') },
                        removeConsequent: { label: I18n.t('personalizedSequence.rules.removeConsequent') }
                    }}
                    allowDefaultConsequents={false}
                    autoSelectConsequentType
                    components={{
                        rulesEngineBuilderHeader: () => null,
                        removeCondition: () => null,
                        addConsequent: () => null,
                        removeConsequent: () => null,
                        addSubcondition: () => null,
                        addCondition: () => null
                    }}
                />
            </QueryBuilderStateProvider>

            <div style={{ marginTop: '2rem' }}>
                <Header as='h4' dividing>Parámetros de las Acciones</Header>
                {rulesEngine.conditions.map((condition, idx) => (
                    <RuleActionParams
                        key={idx}
                        index={idx}
                        condition={condition}
                        onUpdate={updateConsequentData}
                    />
                ))}
            </div>

            <Button className="ruleFormSave" type='submit' primary>Guardar Regla</Button>
        </Form>
    );
};

const validate = () => {

    return {};

};

export default reduxForm({
    form: 'ruleForm',
    touchOnBlur: true,
    touchOnChange: true,
    validate,
})(connect(null, {})(RuleForm));
